// Theme state: light | dark | system.
//
// The provider does NOT decide what the first paint looks like — the inline boot
// script in web/index.html already did that, before React existed. Its job is to
// keep <html> in sync afterwards: when the user picks a theme, when the OS
// preference changes under a 'system' user, and when another tab changes it.
//
// Mounted in AppLayout rather than App, because the toggle lives in the top bar
// and nothing above the layout reads theme state — the class on <html> is what
// the CSS actually keys on, and that is already correct by then.

import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import {
  DARK,
  LIGHT,
  SYSTEM,
  THEME_STORAGE_KEY,
  applyTheme,
  readMode,
  resolveTheme,
  subscribeToSystemTheme,
  writeMode,
} from './storage.js';

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  // Seeded from storage rather than from a constant, so the very first render
  // agrees with what the boot script already put on <html>. Seeding with 'light'
  // and correcting in an effect is how dark mode ships with a flash.
  const [mode, setModeState] = useState(readMode);
  const [systemTheme, setSystemTheme] = useState(() => resolveTheme(SYSTEM));

  const theme = mode === SYSTEM ? systemTheme : mode;

  // Follow the OS while it is running, not just at boot. Someone on macOS with
  // "Auto" scheduled will change theme at sunset with the workspace open all day.
  useEffect(() => subscribeToSystemTheme(setSystemTheme), []);

  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  // Agents keep the workspace open in several tabs. Without this, changing the
  // theme in one leaves the others stale until reload.
  useEffect(() => {
    function onStorage(event) {
      if (event.key !== THEME_STORAGE_KEY) return;
      setModeState(readMode());
    }
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setMode = useCallback((next) => {
    const chosen = next === LIGHT || next === DARK ? next : SYSTEM;
    writeMode(chosen);
    setModeState(chosen);
  }, []);

  /**
   * TWO STATES, NOT THREE.
   *
   * The button flips light <-> dark and nothing else. A three-way cycle through
   * light -> dark -> system behind one unlabelled icon makes the third click
   * unpredictable — you cannot tell "dark because I said so" from "dark because
   * my Mac said so" by looking at a moon, and in a tool people sit in all day
   * that unpredictability costs more than the setting is worth.
   *
   * 'system' is still the state everyone starts in, and you can get back to it:
   * if the theme you are switching *to* is the one the OS already wants, we
   * store 'system' instead of an explicit choice, so the app resumes following
   * the OS. Toggling away and back therefore returns you to automatic, with no
   * third icon state and nothing to explain.
   */
  const toggle = useCallback(() => {
    const next = theme === DARK ? LIGHT : DARK;
    setMode(next === resolveTheme(SYSTEM) ? SYSTEM : next);
  }, [theme, setMode]);

  const value = useMemo(
    () => ({ mode, theme, isDark: theme === DARK, setMode, toggle }),
    [mode, theme, setMode, toggle],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

/**
 * `mode`   what the user chose: 'light' | 'dark' | 'system'
 * `theme`  what is actually on screen: 'light' | 'dark'
 * `isDark` shorthand for the above; prefer a CSS token over branching on it
 * `setMode(mode)` explicit choice, persisted
 * `toggle()` flip light/dark, returning to 'system' when the two agree
 */
export function useTheme() {
  const value = useContext(ThemeContext);
  if (!value) throw new Error('useTheme must be used inside <ThemeProvider>');
  return value;
}
